import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useSessions } from '@/lib/sessions';
import {
  Wrench, FileText, MessageSquare, ListChecks, Activity, ArrowUpRight, Shield, FlaskConical, Clock,
} from 'lucide-react';

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="surface p-5 hover:border-gray-300 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="overline text-gray-500">{label}</span>
        <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.8} />
      </div>
      <div className="font-display text-4xl font-black mt-3 tracking-tight">{value}</div>
    </div>
  );
}

function QuickCard({ to, title, subtitle, icon: Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="surface p-6 hover:border-[#002FA7] hover:shadow-md transition-all group block"
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#002FA7] transition-colors" />
      </div>
      <div className="font-display text-xl font-bold mt-4 tracking-tight">{title}</div>
      <div className="text-sm text-gray-500 mt-1">{subtitle}</div>
    </Link>
  );
}

/**
 * Format a "time ago" string for a session updated_at timestamp.
 * Returns short labels like "ora", "5min", "2h", "3g".
 */
function timeAgo(iso, t) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return t('dashboard.now') || 'ora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}g`;
}

/**
 * Build a one-line preview of the session content for the recent-sessions card.
 * - chat: last assistant or user message
 * - guided: current step label or "Risultato" if step === 3
 */
function sessionPreview(s, t) {
  const st = s.state || {};
  if (s.kind === 'chat') {
    const msgs = st.messages || [];
    const last = msgs.length ? msgs[msgs.length - 1] : null;
    if (!last) return t('dashboard.no_messages') || 'Nessun messaggio';
    const role = last.role === 'user' ? '→' : last.role === 'assistant' ? 'AI' : '⚠';
    const text = (last.text || '').replace(/\s+/g, ' ').slice(0, 90);
    return `${role}  ${text || '…'}`;
  }
  // guided
  const step = typeof st.step === 'number' ? st.step : 0;
  if (step === 3 && st.result) {
    return (t('dashboard.result_ready') || 'Risultato pronto') + ` · ${st.symptom || ''}`.trim();
  }
  const labels = [
    t('dashboard.step_brand') || 'Marca',
    t('dashboard.step_model') || 'Modello',
    t('dashboard.step_symptom') || 'Sintomo',
  ];
  return (t('dashboard.step_at') || 'Step') + ` ${step + 1}/3 · ${labels[step] || ''}`;
}

function RecentSessionCard({ session, onClick, t }) {
  const Icon = session.kind === 'chat' ? MessageSquare : ListChecks;
  const kindLabel = session.kind === 'chat' ? (t('chat.title') || 'Chat') : (t('guided.title') || 'Guidata');
  return (
    <button
      onClick={onClick}
      className="surface p-4 text-left hover:border-[#002FA7] hover:shadow-md transition-all group block"
      data-testid={`recent-session-${session.id}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
          <Icon className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
        <span className="overline text-[10px] text-[#002FA7]">{kindLabel}</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-500 font-mono">
          <Clock className="w-3 h-3" /> {timeAgo(session.updated_at, t)}
        </span>
      </div>
      <div className="font-display font-bold text-sm truncate" title={session.title}>
        {session.title || (session.brand + ' ' + session.model).trim() || (t('sessions.untitled') || 'Senza titolo')}
      </div>
      <div className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
        {sessionPreview(session, t)}
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessions, setActive, createSession } = useSessions();
  const [stats, setStats] = useState({ machines: 0, documents: 0, interventions: 0 });

  useEffect(() => {
    Promise.all([
      api.get('/machines'),
      api.get('/documents'),
      api.get('/interventions'),
    ]).then(([m, d, i]) => {
      setStats({ machines: m.data.length, documents: d.data.length, interventions: i.data.length });
    }).catch(() => {});
  }, []);

  // Top 6 most-recent active sessions of the user (already filtered by user_id server-side)
  const recentSessions = useMemo(() => {
    return [...(sessions || [])]
      .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || b.updated_at.localeCompare(a.updated_at))
      .slice(0, 6);
  }, [sessions]);

  const openSession = (s) => {
    setActive(s.kind, s.id);
    navigate(s.kind === 'chat' ? '/diagnosi' : '/diagnosi-guidata');
  };

  const createAndOpen = async (kind) => {
    const s = await createSession(kind);
    navigate(s.kind === 'chat' ? '/diagnosi' : '/diagnosi-guidata');
  };

  return (
    <div className="animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#002FA7]">{t('dashboard.title').toUpperCase()}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">
            {t('dashboard.welcome', { name: user?.full_name?.split(' ')[0] || '' })}
          </h1>
        </div>
        {user?.role === 'admin' && (
          <Link to="/admin" data-testid="dashboard-admin-link" className="btn-secondary">
            <Shield className="w-4 h-4" /> {t('nav.admin')}
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <StatCard label={t('dashboard.machines')} value={stats.machines} icon={Wrench} testid="stat-machines" />
        <StatCard label={t('dashboard.documents')} value={stats.documents} icon={FileText} testid="stat-documents" />
        <StatCard label={t('dashboard.interventions')} value={stats.interventions} icon={Activity} testid="stat-interventions" />
      </div>

      {/* Sessioni recenti — the technician's "resume where I left off" panel.
          Only rendered if at least one active session exists. Otherwise the user
          jumps straight to Quick Actions below. */}
      {recentSessions.length > 0 && (
        <div className="mt-10" data-testid="recent-sessions-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="overline text-gray-500">
              {t('dashboard.recent_sessions') || 'Sessioni recenti'}
              <span className="ml-2 text-[10px] font-mono text-gray-400 normal-case tracking-normal">
                ({sessions.length})
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => createAndOpen('chat')}
                className="text-xs font-mono uppercase text-[#002FA7] hover:underline"
                data-testid="dash-new-chat"
              >
                + {t('chat.title') || 'Chat'}
              </button>
              <button
                onClick={() => createAndOpen('guided')}
                className="text-xs font-mono uppercase text-[#002FA7] hover:underline"
                data-testid="dash-new-guided"
              >
                + {t('guided.title') || 'Guidata'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-sessions-grid">
            {recentSessions.map((s) => (
              <RecentSessionCard
                key={s.id}
                session={s}
                onClick={() => openSession(s)}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="overline text-gray-500 mb-4">{t('dashboard.quick_actions').toUpperCase()}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickCard to="/diagnosi" title={t('chat.title')} subtitle={t('chat.advanced_tag')} icon={MessageSquare} testid="quick-chat" />
          <QuickCard to="/diagnosi-guidata" title={t('guided.title')} subtitle={t('guided.subtitle')} icon={ListChecks} testid="quick-guided" />
          <QuickCard to="/universal-procedures" title={t('universal.title') || 'Procedure universali'} subtitle={t('universal.tagline') || 'Test elettrici standard'} icon={FlaskConical} testid="quick-universal" />
          <QuickCard to="/macchine" title={t('nav.machines')} subtitle="" icon={Wrench} testid="quick-machines" />
        </div>
      </div>
    </div>
  );
}
